import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import SiteHeader from "@/components/SiteHeader";
import Seo, { attractionJsonLd, breadcrumbJsonLd } from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";
import { PARTNERS } from "@/components/PartnerLogos";
import {
  ArrowLeft, MapPin, Clock, Star, ChevronLeft, ChevronRight, X,
  Users, Sparkles, Ticket, Check, CalendarDays
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// -----------------------------------------------------------------
// Fullscreen Lightbox
// -----------------------------------------------------------------
const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [index, onClose, onPrev, onNext]);

  if (index === null) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="attraction-lightbox"
    >
      <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white p-2" aria-label="Fechar">
        <X className="w-6 h-6" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
        aria-label="Anterior"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <img
        src={images[index]}
        alt=""
        className="max-w-[92vw] max-h-[88vh] object-contain rounded-xl"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
        aria-label="Próxima"
      >
        <ChevronRight className="w-6 h-6" />
      </button>
      <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm bg-black/40 px-3 py-1 rounded-full">
        {index + 1} / {images.length}
      </p>
    </div>
  );
};

// -----------------------------------------------------------------
// Hero Carousel (auto + arrows + thumbnails)
// -----------------------------------------------------------------
const HeroCarousel = ({ images, onOpenLightbox, alt }) => {
  const [active, setActive] = useState(0);
  const total = images.length;

  const next = useCallback(() => setActive((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setActive((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    if (total < 2) return undefined;
    const id = setInterval(next, 6000);
    return () => clearInterval(id);
  }, [next, total]);

  if (total === 0) return null;

  return (
    <div className="space-y-3" data-testid="attraction-carousel">
      <div className="relative aspect-[16/9] sm:aspect-[2/1] rounded-2xl overflow-hidden bg-navy/5 group">
        {images.map((src, i) => (
          <img
            key={i}
            src={src}
            alt={`${alt} - ${i + 1}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 cursor-zoom-in ${i === active ? "opacity-100" : "opacity-0"}`}
            onClick={() => onOpenLightbox(active)}
          />
        ))}
        {total > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-navy rounded-full p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Anterior"
              data-testid="carousel-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-navy rounded-full p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Próxima"
              data-testid="carousel-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full">
              {active + 1} / {total}
            </div>
          </>
        )}
      </div>
      {total > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {images.map((src, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`flex-shrink-0 w-20 h-14 sm:w-24 sm:h-16 rounded-lg overflow-hidden border-2 transition-all ${i === active ? "border-gold scale-105" : "border-transparent opacity-70 hover:opacity-100"}`}
              data-testid={`carousel-thumb-${i}`}
            >
              <img src={src} alt={`thumb ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------
// Page
// -----------------------------------------------------------------
export default function AttractionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { t, language } = useLanguage();

  const [attraction, setAttraction] = useState(null);
  const [partnerName, setPartnerName] = useState(null);
  const [matchingPassports, setMatchingPassports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const [attrRes, passportsRes] = await Promise.all([
          axios.get(`${API}/attractions`),
          axios.get(`${API}/passport-types`)
        ]);
        const all = attrRes.data || [];
        const found = all.find((a) =>
          a.id === id ||
          (a.slug && a.slug === id) ||
          (a.slug_en && a.slug_en === id) ||
          (Array.isArray(a.old_slugs) && a.old_slugs.includes(id)) ||
          (Array.isArray(a.old_slugs_en) && a.old_slugs_en.includes(id))
        );
        if (!found) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setAttraction(found);

        // Find passports that include this attraction
        const pts = (passportsRes.data || []).filter(
          (p) => Array.isArray(p.attractions) && p.attractions.includes(found.id)
        );
        setMatchingPassports(pts);

        // Fetch public partner name if attraction has partner_id
        if (found.partner_id) {
          try {
            const pRes = await axios.get(`${API}/attractions/${found.id}/partner`);
            setPartnerName(pRes.data?.name || null);
          } catch {
            // Endpoint may not exist or be public — fail silently
          }
        }
      } catch (e) {
        console.error("Failed to load attraction:", e);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id]);

  // Canonical URL per language: redirects old slugs and normalizes the path (301-equivalent for SPA)
  useEffect(() => {
    if (!attraction) return;
    const ptP = `/experiencias/${attraction.slug || attraction.id}`;
    const enP = `/en/experiences/${attraction.slug_en || attraction.slug || attraction.id}`;
    const expected = language === "en" ? enP : ptP;
    if (routerLocation.pathname !== expected) {
      navigate(expected + routerLocation.search + routerLocation.hash, { replace: true });
    }
  }, [attraction, language, routerLocation.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4ED] flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-navy/50 font-body text-sm">{t("attractions.loading")}</div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  // ---------- Not found ----------
  if (notFound || !attraction) {
    return (
      <div className="min-h-screen bg-[#F7F4ED] flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <h1 className="font-heading text-3xl text-navy mb-3">{t("attractions.notFound")}</h1>
          <Button
            onClick={() => navigate("/")}
            className="bg-navy hover:bg-navy/90 text-white rounded-full px-6"
            data-testid="attraction-back-home"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> {t("attractions.backToList")}
          </Button>
        </div>
        <SiteFooter />
      </div>
    );
  }

  // ---------- Normal render ----------
  const images = (attraction.images && attraction.images.length > 0)
    ? attraction.images
    : (attraction.image_url ? [attraction.image_url] : []);
  const rating = attraction.rating || 4.9;
  const reviews = attraction.reviews || attraction.review_count || 0;
  const duration = attraction.duration || "—";
  const location = attraction.location || "Lagos";
  const price = attraction.price_total || attraction.price || 0;
  const description = (language === "en" && attraction.description_en)
    ? attraction.description_en
    : attraction.description;
  const fullDescription = (language === "en" && attraction.full_description_en)
    ? attraction.full_description_en
    : (attraction.full_description || description);
  const rawHighlights = (language === "en" && attraction.highlights_en) || attraction.highlights || "";
  const highlightsList = (Array.isArray(rawHighlights) ? rawHighlights.join(",") : String(rawHighlights))
    .split(/\r?\n|\s*•\s*|\s*-\s+|,/)
    .map((s) => s.trim())
    .filter(Boolean);

  const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
  const partnerLogo = partnerName
    ? (PARTNERS.find((p) => norm(p.name) === norm(partnerName) || norm(partnerName).includes(norm(p.name)) || norm(p.name).includes(norm(partnerName))) || {}).logo
    : null;

  const slugOrId = attraction.slug || attraction.id;
  const enSlug = attraction.slug_en || slugOrId;
  const ptSeoPath = `/experiencias/${slugOrId}`;
  const enSeoPath = `/en/experiences/${enSlug}`;

  return (
    <div className="min-h-screen bg-[#F7F4ED] flex flex-col" data-testid="attraction-detail-page">
      <Seo
        title={language === "en"
          ? (attraction.seo_title_en || `${attraction.name_en || attraction.name} | Tickets & Experience in Lagos, Algarve`)
          : (attraction.seo_title || `${attraction.name} | Bilhetes e Experiência em Lagos, Algarve`)}
        description={language === "en"
          ? (attraction.seo_description_en || attraction.description_en || description)
          : (attraction.seo_description || description)}
        image={(language === "en"
          ? (attraction.social_image_en || attraction.social_image)
          : attraction.social_image) || attraction.image_url}
        keywords={language === "en" ? attraction.seo_keyword_en : attraction.seo_keyword}
        ptPath={ptSeoPath}
        enPath={enSeoPath}
        noindex={attraction.indexable === false}
        jsonLd={[
          attractionJsonLd(attraction, language === "en" ? enSeoPath : ptSeoPath, language === "en"),
          breadcrumbJsonLd([
            { name: "PASS Algarve", path: "/" },
            { name: language === "en" ? "Experiences" : "Experiências", path: "/#attractions" },
            { name: (language === "en" && attraction.name_en) || attraction.name, path: language === "en" ? `/experiences/${enSlug}` : ptSeoPath },
          ], language === "en"),
        ]}
      />
      <SiteHeader />

      {/* Back link */}
      <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 pt-24 sm:pt-28">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-navy/70 hover:text-navy text-sm font-body transition-colors"
          data-testid="attraction-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> {t("attractions.backToList")}
        </button>
      </div>

      <main className="max-w-6xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8 space-y-10 flex-1">
        {/* Hero: title + carousel */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-5"
        >
          <div className="flex flex-wrap items-center gap-3">
            {attraction.requires_booking && (
              <span className="bg-[#071A35] text-[#D4A85D] text-[11px] uppercase tracking-wider font-bold px-3 py-1 rounded-full inline-flex items-center gap-1.5" data-testid="booking-required-badge">
                <CalendarDays className="w-3 h-3" /> {language === "en" ? "Booking required" : "Agendamento obrigatório"}
              </span>
            )}
            <span className="bg-navy/5 text-navy/70 text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1">
              <MapPin className="w-3 h-3 text-gold" /> {location}
            </span>
            {duration !== "—" && (
              <span className="bg-navy/5 text-navy/70 text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                <Clock className="w-3 h-3 text-gold" /> {duration}
              </span>
            )}
            <span className="bg-amber-50 text-amber-700 text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1">
              <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> {rating}{reviews > 0 ? ` (${reviews})` : ""}
            </span>
            <span className="bg-white border border-[#34E0A1]/60 text-navy text-[11px] font-bold px-3 py-1 rounded-full inline-flex items-center gap-1.5 shadow-sm" data-testid="tripadvisor-badge">
              <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="11" fill="#34E0A1" />
                <circle cx="7.5" cy="13" r="3.6" fill="#ffffff" />
                <circle cx="16.5" cy="13" r="3.6" fill="#ffffff" />
                <circle cx="7.5" cy="13" r="1.6" fill="#004F32" />
                <circle cx="16.5" cy="13" r="1.6" fill="#004F32" />
                <path d="M12 7.6l1.6 1.9h-3.2z" fill="#004F32" />
              </svg>
              <span>Tripadvisor</span>
              <span className="flex gap-[2px]" aria-hidden="true">
                {[1, 2, 3, 4, 5].map((n) => (
                  <span key={n} className={`w-[7px] h-[7px] rounded-full ${n <= Math.round(rating) ? "bg-[#34E0A1]" : "bg-[#34E0A1]/25"}`} />
                ))}
              </span>
              <span>{rating}/5</span>
              <span className="text-[#00aa6c] uppercase tracking-wider">{language === "en" ? "Excellent" : "Excelente"}</span>
            </span>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight" data-testid="attraction-title">
            {(language === "en" && attraction.name_en) || attraction.name}
          </h1>

          {description && (
            <p className="text-navy/70 font-body text-base sm:text-lg leading-relaxed max-w-3xl">
              {description}
            </p>
          )}

          {attraction.requires_booking && (
            <p className="text-navy/80 font-body text-sm bg-[#fdf8ee] border border-gold/40 rounded-lg px-4 py-3 max-w-3xl flex items-start gap-2" data-testid="booking-required-note">
              <CalendarDays className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
              {language === "en"
                ? "This experience requires prior booking of date and time."
                : "Esta experiência requer agendamento prévio de data e horário."}
            </p>
          )}

          {images.length > 0 && (
            <HeroCarousel
              images={images}
              alt={attraction.name}
              onOpenLightbox={(i) => setLightboxIdx(i)}
            />
          )}
        </motion.section>

        {/* Full description + highlights */}
        {(fullDescription || highlightsList.length > 0) && (
          <section className="grid md:grid-cols-3 gap-6">
            {fullDescription && fullDescription !== description && (
              <div className="md:col-span-2 bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-navy/5">
                <h2 className="font-heading text-xl text-navy mb-3 flex items-center gap-2.5">
                  {partnerLogo ? (
                    <img
                      src={partnerLogo}
                      alt={partnerName}
                      className="w-9 h-9 object-contain rounded-full bg-white border border-navy/10 p-0.5 flex-shrink-0"
                      data-testid="about-partner-logo"
                    />
                  ) : (
                    <Sparkles className="w-5 h-5 text-gold" />
                  )}
                  {language === "en" ? "About this experience" : "Sobre esta experiência"}
                </h2>
                <div className="prose prose-sm max-w-none text-navy/75 leading-relaxed whitespace-pre-line font-body">
                  {fullDescription}
                </div>
              </div>
            )}

            {highlightsList.length > 0 && (
              <div className={`bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-navy/5 ${fullDescription && fullDescription !== description ? "" : "md:col-span-3"}`}>
                <h2 className="font-heading text-xl text-navy mb-4 flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-gold" />
                  {t("attractions.highlights")}
                </h2>
                <ul className="space-y-2.5">
                  {highlightsList.map((h, i) => (
                    <li key={i} className="flex gap-2 text-sm text-navy/80 font-body" data-testid={`attraction-highlight-${i}`}>
                      <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* Passports that include this attraction */}
        <section
          className="bg-gradient-to-br from-navy to-[#0a2547] rounded-2xl p-6 sm:p-10 text-white"
          data-testid="attraction-passports-section"
        >
          <div className="flex items-center gap-3 mb-2">
            <Ticket className="w-6 h-6 text-gold" />
            <h2 className="font-heading text-2xl sm:text-3xl">
              {t("attractions.includedInPassports")}
            </h2>
          </div>
          <p className="text-white/60 text-sm mb-6 max-w-2xl font-body">
            {language === "en"
              ? "Choose the passport that best fits your trip and unlock this experience together with others."
              : "Escolha o passaporte que melhor se adapta à sua viagem e desbloqueie esta experiência junto com outras."}
          </p>

          {matchingPassports.length === 0 ? (
            <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-white/70 text-sm font-body">
              {t("attractions.noPassports")}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="matching-passports-grid">
              {matchingPassports.map((p) => (
                <Link
                  key={p.id}
                  to={language === "en"
                    ? `/en/passport/${p.slug_en || p.slug || p.id}`
                    : `/passaporte/${p.slug || p.id}`}
                  className="group bg-white/[0.06] hover:bg-white/[0.1] border border-white/10 hover:border-gold/40 rounded-xl p-5 transition-all"
                  data-testid={`matching-passport-${p.id}`}
                >
                  <div className="text-[10px] uppercase tracking-widest text-gold/90 font-bold mb-1">
                    Pass Algarve
                  </div>
                  <h3 className="font-heading text-lg leading-tight mb-3 group-hover:text-gold transition-colors">
                    {p.name}
                  </h3>
                  <div className="flex items-end justify-between mb-4">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-white/40">
                        {t("attractions.from")}
                      </p>
                      <p className="font-heading text-2xl text-white">€{Number(p.price || 0).toFixed(2)}</p>
                    </div>
                    {p.validity_days && (
                      <span className="text-[10px] uppercase tracking-wider text-white/50">
                        {p.validity_days} {language === "en" ? "days" : "dias"}
                      </span>
                    )}
                  </div>
                  <div className="inline-flex items-center gap-1.5 bg-gold hover:bg-gold-dark text-navy text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full transition-colors">
                    {t("attractions.buyPassport")}
                    <ChevronRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Operated by */}
        {partnerName && (
          <div className="text-center text-navy/50 text-xs font-body uppercase tracking-wider flex items-center justify-center gap-2">
            <Users className="w-3.5 h-3.5" />
            {t("attractions.operatedBy")} <span className="font-semibold text-navy/80">{partnerName}</span>
          </div>
        )}
      </main>

      <SiteFooter />

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <Lightbox
          images={images}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onPrev={() => setLightboxIdx((i) => (i - 1 + images.length) % images.length)}
          onNext={() => setLightboxIdx((i) => (i + 1) % images.length)}
        />
      )}
    </div>
  );
}
